import {
  AdyenCheckout,
  Card,
  type CardConfiguration,
  type CoreConfiguration,
  type SubmitData,
  type UIElement,
} from "@adyen/adyen-web";
import React, { useEffect, useState } from "react";
import { updateMonthlyOrderCreditCard } from "../../../apis/subscription";
import type { IStoredPaymentMethod } from "../../../apis/subscription/type";
import { customShowToast } from "../../../helper/toast";
import useGeneralStore from "../../../store/general";
import BaseButton from "../../atom/baseButton";
import BaseLabel from "../../atom/baseLabel";
import BaseModal, { type TBaseModalProps } from "../../atom/baseModal";
import BaseTextInput from "../../atom/baseTextInput";
import "./index.css";

const ADYEN_CLIENT_KEY = process.env.REACT_APP_ADYEN_CLIENT_KEY;
const ADYEN_ENV = process.env.REACT_APP_ENV === "production" ? "live" : "test";

interface IRelinkSubscriptionCreditCardModal {
  shopperReference: string;
  storedPaymentMethods?: IStoredPaymentMethod | null;
  refetchMonthlyOrder: () => void;
}

type TRelinkSubscriptionCreditCardModalProps =
  IRelinkSubscriptionCreditCardModal & TBaseModalProps;

const RelinkSubscriptionCreditCardModal: React.FC<
  TRelinkSubscriptionCreditCardModalProps
> = (props) => {
  const {
    shopperReference,
    refetchMonthlyOrder,
    storedPaymentMethods,
    open,
    onClose,
    ...otherProps
  } = props;
  const [cardElement, setCardElement] = useState<Card | undefined>(undefined);

  const [isCardAllValid, setIsCardAllValid] = useState(false);

  const setGlobalLoadingState = useGeneralStore((state) => state.setIsLoading);

  const initAdyen = async () => {
    try {
      // Configuration for AdyenCheckout
      const configuration: CoreConfiguration = {
        clientKey: ADYEN_CLIENT_KEY,
        environment: ADYEN_ENV,
        locale: "en-US", // Locale for the payment form
        countryCode: "HK",
        showPayButton: false,

        risk: {
          enabled: false,
        },
        analytics: {
          enabled: false,
        },
        onError(error, component) {
          console.error("Error", error);
        },
      };

      // Initialize AdyenCheckout
      const checkout = await AdyenCheckout(configuration);

      const customCardConfig: CardConfiguration = {
        styles: {
          error: {
            color: "red",
          },
          validated: {
            color: "green",
          },
          placeholder: {
            fontSize: "12px",
            color: "black",
            fontWeight: "500",
          },
        },

        placeholders: {
          cardNumber: "Card number",
          expiryDate: "MM / YY",
          securityCodeThreeDigits: "CVC",
        },
        showContextualElement: false,
        onSubmit: async (state, component) => {
          await linkWithCreditCard(state, component);
        },

        onAllValid(event) {
          setIsCardAllValid(true);
        },
      };

      const customCard = new Card(checkout, customCardConfig).mount(
        "#component-container",
      );

      setCardElement(customCard);
    } catch (error) {
      setGlobalLoadingState(false);

      console.log(error);
    } finally {
      setGlobalLoadingState(false);
    }
  };

  const linkWithCreditCard = async (
    state: SubmitData,
    component: UIElement,
  ) => {
    try {
      const match = shopperReference.match(/monthlyOrderId-(\d+)/);
      const monthlyOrderId = match ? parseInt(match[1], 10) : null;

      const loginState = JSON.parse(
        localStorage.getItem("wbloginState") || "{}",
      );

      setGlobalLoadingState(true);
      const bindResult = await updateMonthlyOrderCreditCard(
        {
          encryptedCardNumber: state.data.paymentMethod.encryptedCardNumber,
          encryptedSecurityCode: state.data.paymentMethod.encryptedSecurityCode,
          encryptedExpiryYear: state.data.paymentMethod.encryptedExpiryYear,
          encryptedExpiryMonth: state.data.paymentMethod.encryptedExpiryMonth,
          clientData: state.data.riskData?.clientData,
          monthlyOrderId: monthlyOrderId?.toString(),
        },
        loginState.t,
      );

      const errorCode = [
        "Refused",
        "Error",
        "Cancelled",
        "Pending",
        "ChallengeShopper",
        "IdentifyShopper",
        "RedirectShopper",
      ];

      if (bindResult && errorCode.includes(bindResult.resultCode!)) {
        customShowToast("綁定失敗");
        setGlobalLoadingState(false);
      } else {
        customShowToast(`綁定成功`, "success");

        refetchMonthlyOrder();
        onClose();
      }

      setGlobalLoadingState(false);
    } catch (error) {
      cardElement?.setElementStatus("error");
      cardElement?.setStatus("error");
      setGlobalLoadingState(false);
    } finally {
      if (cardElement) {
        cardElement.setElementStatus("ready");
        cardElement.setStatus("ready");
      }
      setGlobalLoadingState(false);
    }
  };

  const bindCardHandler = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (cardElement) {
      cardElement.submit();
    }
  };

  // init adyen card element when tab changed
  useEffect(() => {
    if (open === true) {
      initAdyen();
    }
  }, [open]);

  return (
    <BaseModal
      open={open}
      showCloseButton={true}
      onClose={onClose}
      title={"重新綁定信用卡"}
      titleStyle="text-white font-bold text-2xl"
      modalBaseStyle="bg-black w-96"
      {...otherProps}
    >
      {storedPaymentMethods?.status !== 404 &&
        storedPaymentMethods !== null && (
          <div className="flex flex-col items-start gap-4 mb-4">
            <BaseLabel text="已綁定的信用卡" />
            <div className="flex flex-col gap-4 text-white w-full">
              <BaseTextInput
                placeholder="信用卡號碼"
                value={`xxxx xxxx xxxx ${storedPaymentMethods?.lastFour}`}
                disabled
              />
              <BaseTextInput
                placeholder="信用卡到期日"
                value={`${storedPaymentMethods?.expiryMonth}/${storedPaymentMethods?.expiryYear}`}
                disabled
              />
            </div>
          </div>
        )}

      <BaseLabel
        className="text-mainYellow text-left"
        text="*新增的信用卡將會成為預設付款方式，而原有的信用卡將會被移除。"
      />

      <div className="flex flex-col gap-4 text-white">
        <div className="flex flex-row justify-center items-center gap-2 ">
          <div
            id="component-container"
            className="flex-1 flex text-white min-h-[6.25rem]"
          ></div>
        </div>
        <div className="flex flex-row justify-between items-center gap-4">
          <BaseButton
            onClick={bindCardHandler}
            disabled={!isCardAllValid}
            className="flex-1"
          >
            新增信用卡
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default RelinkSubscriptionCreditCardModal;
